/**
 * Adapter do widget Memed — isola SDK (MdHub / MdSinapsePrescricao) da UI.
 */

import { MEMED_MODULE_NAME } from './memed.config';
import { MEMED_ERROR_CODES, MemedFrontendError } from './memed.errors';
import { loadMemedScript } from './memed-script.loader';
import {
  looksLikeMemedPrescriptionPayload,
  normalizeMemedPrescriptionResult,
} from './memed-prescription.normalizer';
import type {
  MemedHubEventBus,
  MemedOpenPrescriptionInput,
  MemedPrescriptionResult,
} from './memed.types';

/** Eventos do SDK Memed que indicam emissão/salvamento da receita. */
const ISSUE_EVENTS = [
  'prescricaoImpressa',
  'prescricaoSalva',
  'digitalPrescriptionComplete',
  'prescricaoFinalizada',
] as const;

type ListenerBinding = {
  eventName: string;
  handler: (payload?: unknown) => void;
  bus: 'hub' | 'sinapse';
};

type CallPatch = {
  bus: MemedHubEventBus;
  original: ((name: string, data?: unknown, message?: unknown) => void) | undefined;
};

let activeListeners: ListenerBinding[] = [];
let callPatches: CallPatch[] = [];
let openInFlight = false;

function requireHub() {
  if (!window.MdHub || !window.MdSinapsePrescricao) {
    throw new MemedFrontendError({
      code: MEMED_ERROR_CODES.WIDGET_INIT_FAILED,
      message: 'Widget Memed não está disponível.',
      kind: 'technical',
      allowLegacyFallback: true,
    });
  }
  return window.MdHub;
}

function subscribe(
  bus: MemedHubEventBus | undefined,
  eventName: string,
  handler: (payload?: unknown) => void,
  busKind: 'hub' | 'sinapse',
): void {
  if (!bus) return;
  // MdHub moderno: event.listen; builds antigas: event.add (alias).
  if (typeof bus.listen === 'function') {
    bus.listen(eventName, handler);
  } else if (typeof bus.add === 'function') {
    bus.add(eventName, handler);
  } else {
    return;
  }
  activeListeners.push({ eventName, handler, bus: busKind });
}

function clearListeners(): void {
  for (const patch of callPatches) {
    if (patch.original) {
      patch.bus.call = patch.original;
    } else {
      delete patch.bus.call;
    }
  }
  callPatches = [];

  const clearedNames = new Set<string>();
  for (const binding of activeListeners) {
    const key = `${binding.bus}:${binding.eventName}`;
    if (clearedNames.has(key)) continue;
    clearedNames.add(key);
    try {
      const bus =
        binding.bus === 'hub' ? window.MdHub?.event : window.MdSinapsePrescricao?.event;
      // MdHub.remove(name) remove todos os handlers daquele evento.
      bus?.remove?.(binding.eventName);
    } catch {
      // remove pode não existir em todas as versões do SDK
    }
  }
  activeListeners = [];
}

/**
 * Algumas builds não disparam os nomes “clássicos”. Espelhamos event.call
 * para capturar payloads que parecem receita emitida.
 */
function patchEventCallSniffer(
  bus: MemedHubEventBus | undefined,
  onMaybeIssued: (eventName: string, payload: unknown) => void,
): void {
  if (!bus || typeof bus.call !== 'function') return;
  const original = bus.call.bind(bus);
  bus.call = (name: string, data?: unknown, message?: unknown) => {
    try {
      if (
        typeof name === 'string' &&
        !name.startsWith('core:') &&
        looksLikeMemedPrescriptionPayload(data)
      ) {
        onMaybeIssued(name, data);
      }
    } catch {
      // sniffer nunca pode quebrar o SDK
    }
    return original(name, data, message);
  };
  callPatches.push({ bus, original });
}

/**
 * Inicializa script (se preciso) e abre o módulo plataforma.prescricao.
 * Spike: atestado e exame usam o mesmo módulo/comando `newPrescription`
 * (não há newCertificate/newExam na doc pública). O médico escolhe o tipo no widget.
 * Mutuamente exclusivo: não permite abertura paralela.
 */
export async function openMemedDocument(
  input: MemedOpenPrescriptionInput,
): Promise<void> {
  return openMemedPrescription(input);
}

/**
 * Inicializa script (se preciso) e abre newPrescription para o paciente.
 * Mutuamente exclusivo: não permite abertura paralela.
 * Renovação (T10): mesma abertura com paciente pré-preenchido; médico revisa antes de emitir.
 * Não usa viewPrescription (evitaria nova emissão / risco de alterar original).
 */
export async function openMemedPrescription(
  input: MemedOpenPrescriptionInput,
): Promise<void> {
  if (openInFlight) {
    return;
  }
  openInFlight = true;

  try {
    clearListeners();
    await loadMemedScript(input.token);

    const hub = requireHub();
    let issued = false;
    const seenIds = new Set<string>();

    const handleIssued = (eventName: string, fromSniffer = false) => (payload?: unknown) => {
      const result: MemedPrescriptionResult = normalizeMemedPrescriptionResult(
        payload,
        eventName,
      );

      // Sniffer: só propaga quando há ID confiável (evita falso positivo).
      if (fromSniffer && !result.providerDocumentId) {
        return;
      }

      if (!result.providerDocumentId) {
        console.warn(
          JSON.stringify({
            provider: 'MEMED',
            event: 'memed_issue_missing_id',
            eventName,
            payloadType: Array.isArray(payload) ? 'array' : typeof payload,
            payloadKeys:
              payload && typeof payload === 'object' && !Array.isArray(payload)
                ? Object.keys(payload as object).slice(0, 20)
                : [],
          }),
        );
        // Ainda notifica a UI para abrir vínculo manual / corrigir retry.
        input.onIssued?.(result);
        return;
      }

      if (seenIds.has(result.providerDocumentId)) return;
      seenIds.add(result.providerDocumentId);

      issued = true;
      input.onIssued?.(result);
    };

    for (const eventName of ISSUE_EVENTS) {
      const handler = handleIssued(eventName, false);
      subscribe(hub.event, eventName, handler, 'hub');
      subscribe(window.MdSinapsePrescricao?.event, eventName, handler, 'sinapse');
    }

    const sniffer = (eventName: string, payload: unknown) => {
      if ((ISSUE_EVENTS as readonly string[]).includes(eventName)) {
        return;
      }
      handleIssued(eventName, true)(payload);
    };
    patchEventCallSniffer(hub.event, sniffer);
    patchEventCallSniffer(window.MdSinapsePrescricao?.event, sniffer);

    subscribe(
      window.MdSinapsePrescricao?.event,
      'core:moduleHide',
      (module) => {
        const info = module as { moduleName?: string; name?: string } | undefined;
        const name = info?.moduleName || info?.name;
        if (name && name !== MEMED_MODULE_NAME) return;
        if (!issued) {
          input.onCancel?.();
        }
      },
      'sinapse',
    );

    // Prefill suportado de forma confiável pelo SDK integrado: paciente.
    await hub.command.send(MEMED_MODULE_NAME, 'setPaciente', input.patient);
    await hub.module.show(MEMED_MODULE_NAME);
    // Sempre nova prescrição — renovação = nova emissão (médico revisa no widget).
    await hub.command.send(MEMED_MODULE_NAME, 'newPrescription');
  } catch (error) {
    clearListeners();
    if (error instanceof MemedFrontendError) {
      input.onError?.(error.message);
      throw error;
    }
    const wrapped = new MemedFrontendError({
      code: MEMED_ERROR_CODES.WIDGET_INIT_FAILED,
      message: 'Não foi possível abrir a prescrição digital.',
      kind: 'technical',
      allowLegacyFallback: true,
    });
    input.onError?.(wrapped.message);
    throw wrapped;
  } finally {
    openInFlight = false;
  }
}

export function cleanupMemedWidgetListeners(): void {
  clearListeners();
}

export function isMemedPrescriptionOpening(): boolean {
  return openInFlight;
}

/** Test helper */
export function resetMemedWidgetStateForTests(): void {
  clearListeners();
  openInFlight = false;
}
